import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../components/common/Toast';
import { LogOut, ChevronLeft } from 'lucide-react';
import './ProfilePage.css';

const THEME_OPTIONS = ['light', 'dark', 'system'];

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, profile, logout } = useAuth();
  const { themePref, setTheme } = useTheme();
  const toast = useToast();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState(profile?.displayName || '');
  const [language, setLanguage] = useState(profile?.language || i18n.language);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName || '');
      setLanguage(profile.language || i18n.language);
    }
  }, [profile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, COLLECTIONS.USERS, user.uid), {
        displayName: displayName.trim(),
        language,
        updatedAt: serverTimestamp(),
      });
      i18n.changeLanguage(language);
      toast(t('settings.saved'), 'success');
    } catch {
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(profile?.role === 'admin' ? '/admin' : '/production');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <button className="btn btn-icon" onClick={handleBack}><ChevronLeft size={22} /></button>
        <h2 className="font-semibold">{t('common.profile')}</h2>
        <div style={{ width: 38 }} />
      </div>

      {/* Avatar */}
      <div className="profile-avatar">
        <div className="profile-avatar-circle">{profile?.displayName?.[0] ?? '?'}</div>
        <div className="profile-avatar-info">
          {profile?.displayName && (
            <p className="profile-avatar-name">{profile.displayName}</p>
          )}
          <p className="text-muted text-sm">{profile?.email}</p>
        </div>
        <span className={`badge badge-${profile?.role === 'admin' ? 'admin' : 'production'}`}>
          {t(`users.${profile?.role}`)}
        </span>
      </div>

      {/* Account section */}
      <div className="profile-section">
        <div className="profile-section-header">
          <span className="profile-section-title">{t('settings.account')}</span>
        </div>
        <div className="profile-section-body">
          <div className="form-group">
            <label className="form-label">{t('users.displayName')}</label>
            <input
              className="form-control form-control-lg"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Preferences section */}
      <div className="profile-section">
        <div className="profile-section-header">
          <span className="profile-section-title">{t('settings.preferences')}</span>
        </div>
        <div className="profile-section-body">
          <div className="profile-option-row">
            <div>
              <div className="profile-option-label">{t('settings.language')}</div>
            </div>
            <select
              className="form-control profile-option-select"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {[
                { value: 'en', label: t('languages.en') },
                { value: 'vi', label: t('languages.vi') },
              ].map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="profile-option-row">
            <div>
              <div className="profile-option-label">{t('settings.theme')}</div>
            </div>
            <div className="theme-pill-group">
              {THEME_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`theme-pill ${themePref === opt ? 'selected' : ''}`}
                  onClick={() => setTheme(opt)}
                >
                  {t(`settings.${opt}`)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save */}
      <button className="btn btn-primary btn-lg btn-block" onClick={handleSave} disabled={saving}>
        {saving ? <span className="spinner" /> : t('common.save')}
      </button>

      {/* Sign out */}
      <div className="profile-signout">
        <button className="btn btn-danger-ghost btn-block" onClick={handleLogout}>
          <LogOut size={18} />
          {t('common.logout')}
        </button>
      </div>
    </div>
  );
}
